/**
 * @file: index.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Log a manual outreach attempt on a lead (channel + outcome + notes). The
 * server records it as an `outreach` activity on the timeline. The surface
 * borrows the lead-detail design language (the `LD` palette + card elevation)
 * so the two screens read as one app. Thin composition root: wire the form
 * hook, lay out the channel selector / outcome list / reschedule picker /
 * remark field / CTA.
 */

import { MediaComposeRow } from '@euler/app/flows/leads/pages/detail/components/MediaComposeRow';
import { LD } from '@euler/app/flows/leads/pages/detail/components/leadDetailKit';
import { ChannelSegment } from '@euler/app/flows/leads/pages/log-activity/components/ChannelSegment';
import { OutcomeList } from '@euler/app/flows/leads/pages/log-activity/components/OutcomeList';
import { PostponePicker } from '@euler/app/flows/leads/pages/log-activity/components/PostponePicker';
import { RemarkField } from '@euler/app/flows/leads/pages/log-activity/components/RemarkField';
import { SaveButton } from '@euler/app/flows/leads/pages/log-activity/components/SaveButton';
import { Card } from '@euler/app/flows/leads/pages/log-activity/components/common/Card';
import { Eyebrow } from '@euler/app/flows/leads/pages/log-activity/components/common/Eyebrow';
import { useLogActivityForm } from '@euler/app/flows/leads/pages/log-activity/functions/useLogActivityForm';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsDesktopLayout } from '@euler/functions/responsiveness';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { ScrollView, View } from 'react-native';

export const LogLeadActivityScreen = wrapNavigatorScreen(
  ({ leadId }: { leadId: string }) => {
    const { rpx } = useResponsiveContext();
    const isDesktop = useIsDesktopLayout();
    const form = useLogActivityForm(leadId);

    return (
      <View style={{ flex: 1, backgroundColor: LD.canvas }}>
        <ScrollView
          contentContainerStyle={{ padding: rpx(14), paddingBottom: rpx(32) }}
        >
          <View
            style={
              isDesktop
                ? { width: '100%', maxWidth: rpx(560), alignSelf: 'center' }
                : undefined
            }
          >
            <Eyebrow first>跟进方式</Eyebrow>
            <ChannelSegment
              channel={form.channel}
              onChange={form.onChannelChange}
            />

            {form.outcomes.length ? (
              <>
                <Eyebrow required>跟进结果</Eyebrow>
                <OutcomeList
                  outcomes={form.outcomes}
                  outcome={form.outcome}
                  onToggle={form.toggleOutcome}
                />
              </>
            ) : null}

            {form.isPostponed ? (
              <>
                <Eyebrow required>改期至</Eyebrow>
                <PostponePicker
                  value={form.postponeDate}
                  onChange={form.setPostponeAt}
                />
              </>
            ) : null}

            <Eyebrow>备注</Eyebrow>
            <RemarkField value={form.notes} onChangeText={form.setNotes} />

            <Eyebrow>照片 / 视频</Eyebrow>
            <Card>
              <View style={{ paddingVertical: rpx(14) }}>
                <MediaComposeRow
                  items={form.uploader.items}
                  onAdd={form.uploader.pick}
                  onRemove={form.uploader.remove}
                />
              </View>
            </Card>

            <SaveButton onPress={form.onSubmit} disabled={!form.canSubmit} />
          </View>
        </ScrollView>
      </View>
    );
  },
  {
    title: '记录跟进',
  },
);
